/**
 * frenzy.ts — How wound up the dog is, as pure functions.
 *
 * One number runs this whole mechanic: **how close the nearest critter is to
 * the dog**, as a 0..1 pull. Everything the player can see or hear is that
 * number read a different way —
 *
 *   pull 0 ─────────────────────────────────────────────────────── pull 1
 *   critter at HYPER_PULL_RADIUS                        critter nose-to-nose
 *   dog ambles                                          dog hauls (HYPER_SPEED)
 *   occasional bark (BARK_MAX_MS apart)                 machine-gun barking
 *   lead dark and sagging                               lead bright and straight
 *   meter creeping                                      meter climbing fast
 *
 * — and when the meter reaches 1 the dog wins and the walk is over. Keeping all
 * of it downstream of a single input is what stops the barking, the pulling and
 * the bar from disagreeing with each other on screen.
 *
 * Renderer- and entity-free so it can be unit-tested on its own; the tuning
 * lives in config/constants.ts → FRENZY, DOG and HAZARD.
 */
import { clamp } from '../utils/math';

/**
 * How hard a critter at `dist` pulls, as 0..1.
 *
 * Linear, and deliberately so: the player has to be able to *feel* the gradient
 * while backing away, and a curve that dumps most of its range into the last
 * few units reads as an on/off switch instead. 0 at the edge of the radius (and
 * beyond it), 1 at zero distance.
 */
export function critterPull(dist: number, radius: number): number {
  if (radius <= 0) return 0;
  return clamp(1 - dist / radius, 0, 1);
}

/** Interpolate the dog's lunge speed between its idle-lunge and full-yank ends. */
export function lungeSpeed(pull: number, minSpeed: number, maxSpeed: number): number {
  return minSpeed + (maxSpeed - minSpeed) * clamp(pull, 0, 1);
}

/**
 * Milliseconds until the next bark at this pull — long gaps at the edge of the
 * radius, a rattle of them when the critter is right there.
 *
 * `pull` of 0 means nothing to bark at; the caller is expected not to ask, but
 * it still answers with the longest gap rather than zero, so a mis-timed call
 * can never turn into a bark every frame.
 */
export function barkIntervalMs(pull: number, minMs: number, maxMs: number): number {
  return maxMs - (maxMs - minMs) * clamp(pull, 0, 1);
}

/** Everything `stepFrenzy` needs from `FRENZY`. */
export interface FrenzyTuning {
  readonly RISE_PER_SEC: number;
  readonly DECAY_PER_SEC: number;
  readonly CALM_DELAY_MS: number;
}

/** The meter, plus how long it has been since anything last wound the dog up. */
export interface FrenzyState {
  /** 0 = calm dog, 1 = the dog is gone and the run is over. */
  readonly level: number;
  /** Milliseconds since the last frame with any pull at all. */
  readonly calmMs: number;
}

/** An empty meter — where every run starts. */
export function initialFrenzy(): FrenzyState {
  return { level: 0, calmMs: 0 };
}

/**
 * Advance the meter one frame.
 *
 * Rising is proportional to the pull, so a distant cat barely moves it and a
 * nose-to-nose one fills it in `1 / RISE_PER_SEC` seconds. Falling is flat and
 * faster, but only after `CALM_DELAY_MS` of genuine peace — long enough that
 * a critter flickering in and out of range at the boundary can't be used to
 * hold the bar still for free.
 */
export function stepFrenzy(
  state: FrenzyState,
  pull: number,
  deltaMs: number,
  tuning: FrenzyTuning,
): FrenzyState {
  const dt = deltaMs / 1000;
  const p = clamp(pull, 0, 1);

  if (p > 0) {
    return { level: clamp(state.level + tuning.RISE_PER_SEC * p * dt, 0, 1), calmMs: 0 };
  }

  const calmMs = state.calmMs + deltaMs;
  if (calmMs < tuning.CALM_DELAY_MS) return { level: state.level, calmMs };
  return { level: clamp(state.level - tuning.DECAY_PER_SEC * dt, 0, 1), calmMs };
}

/** Has the dog won? The run ends the moment the meter tops out. */
export function frenzyMaxed(state: FrenzyState): boolean {
  return state.level >= 1;
}
